import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchContacts, createContact, updateContact, deleteContact, setPage, setItemsPerPage } from '../store/slices/contactSlice';
import { fetchContactCategories } from '../store/slices/contactCategorySlice';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon, EnvelopeIcon, EyeIcon, TagIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import CountrySelect from '../components/CountrySelect';
import ReactCountryFlag from 'react-country-flag';
import { getCountryCode } from '../utils/countries';
import { INSTITUTION_TYPES } from '../utils/constants';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import api from '../utils/api';

const GOV_PRIVATE = ['Government', 'Private', 'Public', 'N/A'];
const PRIORITY_LEVELS = ['High', 'Medium', 'Low'];
const RELATIONSHIP_STAGES = ['Cold', 'Introduced', 'In discussion', 'MoU signed', 'Active collaboration', 'Dormant'];

// Helper function to get priority badge color
const getPriorityBadgeColor = (priority) => {
  const colors = {
    'High': 'bg-red-100 text-red-800 border-red-200',
    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Low': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Helper function to get relationship stage badge color
const getRelationshipStageBadgeColor = (stage) => {
  const colors = {
    'Cold': 'bg-blue-100 text-blue-800 border-blue-200',
    'Introduced': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'In discussion': 'bg-purple-100 text-purple-800 border-purple-200',
    'MoU signed': 'bg-orange-100 text-orange-800 border-orange-200',
    'Active collaboration': 'bg-green-100 text-green-800 border-green-200',
    'Dormant': 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const Contacts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    items,
    loading,
    total,
    currentPage,
    itemsPerPage,
    totalPages
  } = useSelector((state) => state.contacts);
  const { items: categories } = useSelector((state) => state.contactCategories);
  const { user } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [emailInputs, setEmailInputs] = useState(['']);
  const [contactNumberInputs, setContactNumberInputs] = useState(['']);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [relationshipStageFilter, setRelationshipStageFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [blockCodeFilter, setBlockCodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, contact: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    primaryName: '',
    name: '',
    contactIndividual: '',
    country: '',
    region: '',
    city: '',
    website: '',
    institutionType: '',
    categoryId: '',
    customCategoryType: '',
    govPrivate: 'N/A',
    otherInfo: '',
    contactedOn: '',
    contactedBy: '',
    contactedTo: '',
    institutionName: '',
    designation: '',
    department: '',
    priority: 'Medium',
    relationshipStage: 'Cold',
    blockCode: ''
  });

  useEffect(() => {
    // Fetch categories for the dropdown
    dispatch(fetchContactCategories());
  }, [dispatch]);

  useEffect(() => {
    const filters = {
      page: currentPage,
      limit: itemsPerPage
    };

    if (priorityFilter) filters.priority = priorityFilter;
    if (relationshipStageFilter) filters.relationshipStage = relationshipStageFilter;
    if (countryFilter) filters.country = countryFilter;
    if (categoryFilter) filters.categoryId = categoryFilter;
    if (blockCodeFilter) filters.blockCode = blockCodeFilter;
    if (searchQuery) filters.search = searchQuery;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;

    dispatch(fetchContacts(filters));
  }, [dispatch, currentPage, itemsPerPage, priorityFilter, relationshipStageFilter, countryFilter, categoryFilter, blockCodeFilter, searchQuery, dateFrom, dateTo]);

  const handleAddEmail = () => {
    setEmailInputs([...emailInputs, '']);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emailInputs];
    newEmails[index] = value;
    setEmailInputs(newEmails);
  };

  const handleRemoveEmail = (index) => {
    setEmailInputs(emailInputs.filter((_, i) => i !== index));
  };

  const handleAddContactNumber = () => {
    setContactNumberInputs([...contactNumberInputs, '']);
  };

  const handleContactNumberChange = (index, value) => {
    const newNumbers = [...contactNumberInputs];
    newNumbers[index] = value;
    setContactNumberInputs(newNumbers);
  };

  const handleRemoveContactNumber = (index) => {
    setContactNumberInputs(contactNumberInputs.filter((_, i) => i !== index));
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      primaryName: contact.primaryName || '',
      name: contact.name || '',
      contactIndividual: contact.contactIndividual || '',
      country: contact.country || '',
      region: contact.region || '',
      city: contact.city || '',
      website: contact.website || '',
      institutionType: contact.institutionType || '',
      categoryId: contact.categoryId || '',
      customCategoryType: contact.customCategoryType || '',
      govPrivate: contact.govPrivate || 'N/A',
      otherInfo: contact.otherInfo || '',
      contactedOn: contact.contactedOn ? contact.contactedOn.split('T')[0] : '',
      contactedBy: contact.contactedBy || '',
      contactedTo: contact.contactedTo || '',
      institutionName: contact.institutionName || '',
      designation: contact.designation || '',
      department: contact.department || '',
      priority: contact.priority || 'Medium',
      relationshipStage: contact.relationshipStage || 'Cold',
      blockCode: contact.blockCode || ''
    });
    setEmailInputs(contact.emails && contact.emails.length > 0 ? contact.emails : ['']);
    setContactNumberInputs(contact.contactNumbers && contact.contactNumbers.length > 0 ? contact.contactNumbers : ['']);
    setShowModal(true);
  };

  const handleView = (contact) => {
    setViewingContact(contact);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const emails = emailInputs.filter(email => email.trim() !== '');
      const contactNumbers = contactNumberInputs.filter(number => number.trim() !== '');

      // Clean up form data - convert empty strings to null
      const cleanFormData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        acc[key] = (typeof value === 'string' && value.trim() === '') ? null : value;
        return acc;
      }, {});

      const contactData = { ...cleanFormData, emails, contactNumbers };

      if (editingContact) {
        await dispatch(updateContact({ id: editingContact.id, data: contactData })).unwrap();
        toast.success('Contact updated successfully');
      } else {
        await dispatch(createContact(contactData)).unwrap();
        toast.success('Contact created successfully');
      }

      setShowModal(false);
      setEditingContact(null);
      setFormData({
        primaryName: '',
        name: '',
        contactIndividual: '',
        country: '',
        region: '',
        city: '',
        website: '',
        institutionType: '',
        categoryId: '',
        customCategoryType: '',
        govPrivate: 'N/A',
        otherInfo: '',
        contactedOn: '',
        contactedBy: '',
        contactedTo: '',
        institutionName: '',
        designation: '',
        department: '',
        priority: 'Medium',
        relationshipStage: 'Cold',
        blockCode: ''
      });
      setEmailInputs(['']);
      setContactNumberInputs(['']);
    } catch (err) {
      toast.error(editingContact ? 'Failed to update contact' : 'Failed to create contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (contact) => {
    setDeleteDialog({ isOpen: true, contact });
  };

  const handleSendEmail = (contact) => {
    // Navigate to compose email page with contact pre-selected
    navigate(`/compose-email?contactId=${contact.id}`);
  };

  const confirmDelete = async () => {
    if (deleteDialog.contact) {
      try {
        await dispatch(deleteContact(deleteDialog.contact.id)).unwrap();
        toast.success('Contact deleted successfully');
        setDeleteDialog({ isOpen: false, contact: null });
      } catch (err) {
        toast.error('Failed to delete contact');
        setDeleteDialog({ isOpen: false, contact: null });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ isOpen: false, contact: null });
  };

  // Export functions
  const [exportingMyContacts, setExportingMyContacts] = useState(false);
  const [exportingAllContacts, setExportingAllContacts] = useState(false);

  const handleExportMyContacts = async () => {
    try {
      setExportingMyContacts(true);
      const response = await api.get('/contacts/export', {
        params: {
          myContactsOnly: 'true'
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MyContacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Your contacts exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export data');
      } else {
        toast.error('Failed to export contacts');
      }
    } finally {
      setExportingMyContacts(false);
    }
  };

  const handleExportAllContacts = async () => {
    try {
      setExportingAllContacts(true);
      const response = await api.get('/contacts/export', {
        params: {
          myContactsOnly: 'false'
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AllContacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('All contacts exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to export data');
      } else {
        toast.error('Failed to export contacts');
      }
    } finally {
      setExportingAllContacts(false);
    }
  };

  const canCreate = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canEdit = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canDelete = user?.canDeleteContacts === true;

  // Pagination handlers
  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    dispatch(setItemsPerPage(newItemsPerPage));
  };

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      dispatch(setPage(1));
    }
  }, [priorityFilter, relationshipStageFilter, countryFilter, categoryFilter, blockCodeFilter, searchQuery, dateFrom, dateTo]);

  // Use items directly from Redux store (already filtered by backend)
  const filteredContacts = items || [];

  return (
    <div className="space-y-6">
      {/* Compact Header with Actions */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Title */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Contacts</h1>
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">
              {total || 0} contacts
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {user?.canExportExcel && (
              <>
                <button
                  onClick={handleExportMyContacts}
                  disabled={exportingMyContacts}
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export contacts added by you"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                  {exportingMyContacts ? 'Exporting...' : 'My Contacts'}
                </button>
                <button
                  onClick={handleExportAllContacts}
                  disabled={exportingAllContacts}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export all contacts you have access to"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                  {exportingAllContacts ? 'Exporting...' : 'All Contacts'}
                </button>
              </>
            )}
            {canCreate && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-md"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add Contact
              </button>
            )}
          </div>
        </div>

        {/* Compact Filters Section */}
        <div className="mt-4 space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Organization or Contact Person..."
              className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Priority:</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              >
                <option value="">All Priorities</option>
                {PRIORITY_LEVELS.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Relationship:</label>
              <select
                value={relationshipStageFilter}
                onChange={(e) => setRelationshipStageFilter(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              >
                <option value="">All Stages</option>
                {RELATIONSHIP_STAGES.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1 flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Country:</label>
              <CountrySelect
                value={countryFilter}
                onChange={(countryName) => setCountryFilter(countryName)}
                placeholder="All Countries"
                isClearable={true}
                className="text-xs"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              >
                <option value="">All Categories</option>
                {categories.filter(cat => cat.isActive).map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Block Code:</label>
              <input
                type="text"
                value={blockCodeFilter}
                onChange={(e) => setBlockCodeFilter(e.target.value)}
                placeholder="Search block code..."
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">From Date:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">To Date:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              />
            </div>

            {(dateFrom || dateTo) && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-transparent">Clear</label>
                <button
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium border border-red-200"
                >
                  Clear Dates
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto min-w-full">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region/State</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Individual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Block Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relationship Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emails</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Numbers</th>
                  {(canEdit || canDelete) && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 border-l border-gray-200 shadow-lg">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{contact.primaryName}</div>
                        {contact.name && <div className="text-sm text-gray-500">{contact.name}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {contact.institutionType || contact.category || '-'}
                      </span>
                      {contact.customCategoryType && (
                        <div className="mt-1">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            {contact.customCategoryType}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <ReactCountryFlag
                          countryCode={contact.country ? (getCountryCode(contact.country) || 'XX') : 'XX'}
                          svg
                          style={{
                            width: '1.2em',
                            height: '1.2em',
                            marginRight: '8px'
                          }}
                        />
                        {contact.country || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.region || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.city || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.website ? (
                        <a
                          href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {contact.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.contactIndividual || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.institutionName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.designation || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.department || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.blockCode || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPriorityBadgeColor(contact.priority || 'Medium')}`}>
                        {contact.priority || 'Medium'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRelationshipStageBadgeColor(contact.relationshipStage || 'Cold')}`}>
                        {contact.relationshipStage || 'Cold'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {contact.emails && contact.emails.length > 0 ? (
                        <div className="space-y-1">
                          {contact.emails.slice(0, 2).map((email, idx) => (
                            <div key={idx} className="text-xs">{email}</div>
                          ))}
                          {contact.emails.length > 2 && (
                            <div className="text-xs text-gray-400">+{contact.emails.length - 2} more</div>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {contact.contactNumbers && contact.contactNumbers.length > 0 ? (
                        <div className="space-y-1">
                          {contact.contactNumbers.slice(0, 2).map((number, idx) => (
                            <div key={idx} className="text-xs">
                              <a
                                href={`tel:${number}`}
                                className="text-blue-600 hover:text-blue-800 underline"
                                title={`Call ${number}`}
                              >
                                {number}
                              </a>
                            </div>
                          ))}
                          {contact.contactNumbers.length > 2 && (
                            <div className="text-xs text-gray-400">+{contact.contactNumbers.length - 2} more</div>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    {(canEdit || canDelete) && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 border-l border-gray-200 shadow-lg">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(contact)}
                            className="text-gray-600 hover:text-gray-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleSendEmail(contact)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Send Email"
                          >
                            <EnvelopeIcon className="h-5 w-5" />
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(contact)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(contact)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Create Contact Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full mx-4 sm:mx-0">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                    {editingContact ? 'Edit Contact' : 'Add New Contact'}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Name</label>
                      <input
                        type="text"
                        value={formData.primaryName}
                        onChange={(e) => setFormData({ ...formData, primaryName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Organization/Institution name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Secondary name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Individual</label>
                      <input
                        type="text"
                        value={formData.contactIndividual}
                        onChange={(e) => setFormData({ ...formData, contactIndividual: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Contact person name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Institution Name</label>
                      <input
                        type="text"
                        value={formData.institutionName}
                        onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Name of the institution/organization"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Designation / Title</label>
                      <input
                        type="text"
                        value={formData.designation}
                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Job title or designation"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Department within the institution"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Block Code</label>
                      <input
                        type="text"
                        value={formData.blockCode}
                        onChange={(e) => setFormData({ ...formData, blockCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter block code"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <CountrySelect
                        value={formData.country}
                        onChange={(countryName) => {
                          setFormData({ ...formData, country: countryName });
                        }}
                        placeholder="Select country..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Region/State</label>
                      <input
                        type="text"
                        value={formData.region}
                        onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter region or state"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter city"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Institution Type</label>
                      <select
                        value={formData.institutionType}
                        onChange={(e) => setFormData({ ...formData, institutionType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select institution type</option>
                        {INSTITUTION_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={formData.categoryId}
                        onChange={(e) => {
                          setFormData({ ...formData, categoryId: e.target.value });
                          // Clear custom type if not "Others"
                          const selectedCategory = categories.find(cat => cat.id === parseInt(e.target.value));
                          if (selectedCategory?.name !== 'Others') {
                            setFormData(prev => ({ ...prev, customCategoryType: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select category (optional)</option>
                        {categories.filter(cat => cat.isActive).map(category => (
                          <option key={category.id} value={category.id}>
                            <span style={{ color: category.color }}>●</span> {category.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        e.g., Medical, Law, Engineering - helps categorize the institution
                      </p>

                      {/* Show custom type input when "Others" is selected */}
                      {(() => {
                        const selectedCategory = categories.find(cat => cat.id === parseInt(formData.categoryId));
                        console.log('Selected Category:', selectedCategory);
                        console.log('Category ID:', formData.categoryId);
                        console.log('Is Others?:', selectedCategory?.name === 'Others');
                        return selectedCategory?.name === 'Others';
                      })() && (
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Category Type *</label>
                            <input
                              type="text"
                              value={formData.customCategoryType}
                              onChange={(e) => setFormData({ ...formData, customCategoryType: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter custom category type (e.g., Sports, Music, etc.)"
                              required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Specify the custom category type for this contact
                            </p>
                          </div>
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
                      <select
                        value={formData.govPrivate}
                        onChange={(e) => setFormData({ ...formData, govPrivate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select sector</option>
                        {GOV_PRIVATE.map(gp => (
                          <option key={gp} value={gp}>{gp}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {PRIORITY_LEVELS.map(priority => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Stage</label>
                      <select
                        value={formData.relationshipStage}
                        onChange={(e) => setFormData({ ...formData, relationshipStage: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {RELATIONSHIP_STAGES.map(stage => (
                          <option key={stage} value={stage}>{stage}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted On</label>
                      <input
                        type="date"
                        value={formData.contactedOn}
                        onChange={(e) => setFormData({ ...formData, contactedOn: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted By</label>
                      <input
                        type="text"
                        value={formData.contactedBy}
                        onChange={(e) => setFormData({ ...formData, contactedBy: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Person who made the contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contacted To</label>
                      <input
                        type="text"
                        value={formData.contactedTo}
                        onChange={(e) => setFormData({ ...formData, contactedTo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Person who was contacted"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Addresses</label>
                      {emailInputs.map((email, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => handleEmailChange(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="email@example.com"
                          />
                          {emailInputs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddEmail}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add another email
                      </button>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Numbers</label>
                      {contactNumberInputs.map((number, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="tel"
                            value={number}
                            onChange={(e) => handleContactNumberChange(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 (555) 123-4567"
                          />
                          {contactNumberInputs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveContactNumber(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddContactNumber}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add another contact number
                      </button>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Other Info</label>
                      <textarea
                        value={formData.otherInfo}
                        onChange={(e) => setFormData({ ...formData, otherInfo: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Additional notes or description"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>{editingContact ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      editingContact ? 'Update Contact' : 'Create Contact'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingContact(null);
                    }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Contact Modal */}
      {viewingContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContact.primaryName || '-'}</p>
                  </div>

                  {viewingContact.name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Secondary Name</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.name}</p>
                    </div>
                  )}

                  {viewingContact.contactIndividual && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactIndividual}</p>
                    </div>
                  )}

                  {viewingContact.institutionName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Institution Name</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.institutionName}</p>
                    </div>
                  )}

                  {viewingContact.designation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Designation / Title</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.designation}</p>
                    </div>
                  )}

                  {viewingContact.department && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Department</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.department}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority Level</label>
                    <span className={`mt-1 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPriorityBadgeColor(viewingContact.priority || 'Medium')}`}>
                      {viewingContact.priority || 'Medium'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Relationship Stage</label>
                    <span className={`mt-1 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRelationshipStageBadgeColor(viewingContact.relationshipStage || 'Cold')}`}>
                      {viewingContact.relationshipStage || 'Cold'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <div className="mt-1 flex items-center">
                      <ReactCountryFlag
                        countryCode={viewingContact.country ? (getCountryCode(viewingContact.country) || 'XX') : 'XX'}
                        svg
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                          marginRight: '8px'
                        }}
                      />
                      <p className="text-sm text-gray-900">{viewingContact.country || 'Unknown'}</p>
                    </div>
                  </div>

                  {viewingContact.region && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Region/State</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.region}</p>
                    </div>
                  )}

                  {viewingContact.city && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.city}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Institution Type</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContact.institutionType || '-'}</p>
                  </div>

                  {viewingContact.customCategoryType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Category Type</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.customCategoryType}</p>
                    </div>
                  )}
                  {viewingContact.website && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Website</label>
                      <p className="mt-1 text-sm text-gray-900">
                        <a
                          href={viewingContact.website.startsWith('http') ? viewingContact.website : `https://${viewingContact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {viewingContact.website}
                        </a>
                      </p>
                    </div>
                  )}

                  {viewingContact.govPrivate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sector</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.govPrivate}</p>
                    </div>
                  )}

                  {viewingContact.contactedOn && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted On</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(viewingContact.contactedOn).toLocaleDateString()}</p>
                    </div>
                  )}

                  {viewingContact.contactedBy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted By</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactedBy}</p>
                    </div>
                  )}

                  {viewingContact.contactedTo && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contacted To</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.contactedTo}</p>
                    </div>
                  )}

                  {viewingContact.emails && viewingContact.emails.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
                      <div className="mt-1 space-y-1">
                        {viewingContact.emails.map((email, idx) => (
                          <p key={idx} className="text-sm text-gray-900">{email}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingContact.contactNumbers && viewingContact.contactNumbers.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Contact Numbers</label>
                      <div className="mt-1 space-y-1">
                        {viewingContact.contactNumbers.map((number, idx) => (
                          <p key={idx} className="text-sm text-gray-900">
                            <a
                              href={`tel:${number}`}
                              className="text-blue-600 hover:text-blue-800 underline"
                              title={`Call ${number}`}
                            >
                              {number}
                            </a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingContact.otherInfo && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Other Information</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContact.otherInfo}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setViewingContact(null)}
                  className="w-full sm:w-auto px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Contact"
        message={`Are you sure you want to delete "${deleteDialog.contact?.primaryName || deleteDialog.contact?.name || 'this contact'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Contacts;
